/**
 * デジタルサイネージ地図設定画面用モジュール
 * @module app/signage/SignageMapSettingPage
 */
define([
    'module',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/dom-style',
    'dojo/text!./templates/SignageMapSettingPage.html',
    'dojo/topic',
    'dojox/lang/functional/object',
    'idis/control/Locator',
    'idis/control/Router',
    'idis/map/IdisMap',
    'idis/service/Requester',
    'idis/view/Loader',
    'idis/view/dialog/DialogChain',
    'idis/view/dialog/IdisDialog',
    'idis/view/page/_PageBase',
    'app/config',
    'app/map/LayerPane',
    'app/map/legend/LegendPane',
    'app/map/legend/GridLegendPane',
    // 以下、変数で受けないモジュール
    'dijit/layout/BorderContainer',
    'dijit/layout/TabContainer',
    'dijit/layout/ContentPane',
], function (module, declare, lang, domStyle, template, topic, df, Locator, Router, IdisMap, Requester, Loader,
    DialogChain, IdisDialog, _PageBase, config, LayerPane, LegendPane, GridLegendPane) {

    return declare(module.id.replace(/\//g, '.'), _PageBase, {
        // 基本クラス
        baseClass: 'idis-Page idis-Page--map',

        // テンプレート文字列
        templateString: template,

        // 画面ID
        _screenId: null,

        // レイヤーID
        _layerId: [],

        // 緯度経度
        _latlng: null,

        // ズーム
        _zoom: null,

        _layerControl: null,

        buildRendering: function () {
            this.inherited(arguments);
        },

        constructor: function () {
            this.chain = DialogChain.get(this);
            this._screenId = Locator.getQuery().screenId;
        },

        startup: function () {
            this.inherited(arguments);

            // 初期表示
            this.initPage();

            // 凡例
            this.own(topic.subscribe('app/monitor/ObservationPanel::showGridLegendLayer',
                lang.hitch(this, function () {
                    this.showGridLegendDialog();
                })));

        },

        // 初期表示
        initPage: function () {

            Requester.get('/api/signage/' + this._screenId).then(lang.hitch(this,function(data) {
                var item = data;
                this._version = item.version;
                // レイヤー
                this._layerId = item.layerId;
                // 緯度経度
                if (item.latitude && item.longitude) {
                    this._latlng = [item.latitude, item.longitude];
                } else {
                    this._latlng = [config.map.latitude, config.map.longitude];
                }
                // ズーム
                if (item.zoom) {
                    this._zoom = item.zoom;
                } else {
                    this._zoom = 11;
                }

                this.screenId.innerHTML = this._screenId;
                this.displayName.set('value', item.displayName);
                this.remarks.set('value', item.remarks);

                // マップを初期化
                this.initMap();
                // レイヤーの表示
                this.displayLayer();

            }), function(error) {
                // 処理失敗時はエラーログを出力し、エラーダイアログを表示
                console.error(error);
                this.chain.infoError(error);
            });
        },

        // ウィジェットを破棄する
        destroy: function () {
            this.inherited(arguments);
        },

        /**
         * マップ・オブジェクトにサイズ変更を通知する。
         */
        notifyResizeToMap: function () {
            // マップにサイズ変更を通知
            this.map.invalidateSize({ pan: false });
        },

        /**
         * コントロール領域を隠す。
         */
        hideControlPane: function () {
            domStyle.set(this.monitorPane.domNode, 'display', '');
            domStyle.set(this.controlPane.domNode, 'display', 'none');
            // 再レイアウト
            this.wrapper.layout();
        },

        /**
         * コントロール領域を表示する。
         */
        showControlPane: function () {
            domStyle.set(this.monitorPane.domNode, 'display', 'none');
            domStyle.set(this.controlPane.domNode, 'display', '');
            // 再レイアウト
            this.wrapper.layout();
        },

        /**
         * 凡例ダイアログを表示する。
         */
        toggleLegendDialog: function () {
            if (!this._legendDialog) {
                // 初回呼び出し時にインスタンス生成
                this._legendDialog = new IdisDialog({
                    noUnderlay: true,
                    title: '凡例',
                    content: new LegendPane()
                });
                // 画面が破棄された際に連れて行く
                this.own(this._legendDialog);
            }
            if (this._legendDialog.open) {
                this._legendDialog.hide();
            } else {
                this._legendDialog.show();
            }
        },

        showGridLegendDialog: function () {
            if (!this._gridLegendDialog) {
                // 初回呼び出し時にインスタンス生成
                this._gridLegendDialog = new IdisDialog({
                    noUnderlay: true,
                    title: '凡例',
                    content: new GridLegendPane()
                });
                // 画面が破棄された際に連れて行く
                this.own(this._gridLegendDialog);
            }
            if (this._gridLegendDialog.open) {
                this._gridLegendDialog.hide();
            } else {
                this._gridLegendDialog.show();
            }
        },

        initMap: function () {

            // マップの初期化
            this.map = new IdisMap(this.id + '-map', {
                config: config.map,
                keyboard: false, // コメント時に+/-が使用できないため
                touchExtend: false, // IE対応
                minZoom: 9,
                maxZoom: 18,
                drawControlTooltips: false
            }).setView(this._latlng, this._zoom);

            // mapのdestroyはremoveでDojoと互換なのでownで消せる
            this.own(this.map);

        },

        displayLayer: function() {
            // ツリーにチェックをつける
            var layerQuery = Locator.getLayerQuery();
            var payload = {layerQuery:layerQuery, layerId:this._layerId};
            topic.publish(module.id + '::checked', payload);
        },

        onSubmit: function() {
            try {
                // フォームのバリデーションを実施
                if (!this.form.validate()) {
                    return false;
                }
                var value = this.form.get('value');
                value.screenId = this._screenId;
                value.version = this._version;
                // レイヤー
                value.layerId = df.keys(Locator.getLayerQuery());
                // 緯度経度
                var center = this.map.getCenter();
                value.latitude = center.lat;
                value.longitude = center.lng;
                // ズーム
                value.zoom = this.map.getZoom();

                // 更新確認ダイアログを表示
                this.chain.confirmPut(function(chain) {
                    var promise = Requester.put('/api/signage/map/'+ this._screenId, {
                        data: value,
                        headers: {'Content-Type': 'application/json; charset=utf-8'},
                        handleAs: 'json',
                        preventCache : true
                    }).then(function() {
                        chain.infoComplete(function() {
                            Router.moveTo('signageAdmin');
                        });
                    }, function(error) {
                        chain.infoError(error);
                    });
                    // ローダーの表示
                    Loader.wait(promise);
                });

            } catch (e) {
                console.error(e);
            }
            return false;
        },

        leave: function() {
            Router.moveTo('signageAdmin');
        }

    });
});
